import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Shield, ArrowLeft, Loader2, Search, UserPlus, MoreVertical, Crown,
  CheckCircle2, XCircle, Mail, Copy, Check, Sparkles, Ban, RotateCcw, Clock, TrendingUp,
} from 'lucide-react';

const INDIGO = '#4F46E5';
const CHARCOAL = '#1C1917';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
};

export default function AdminPage({ token }) {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [pendingId, setPendingId] = useState(null);

  const authHeader = useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  // Entitlement check
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/me`, authHeader);
        setIsAdmin(!!data?.is_admin);
      } catch { setIsAdmin(false); }
    })();
  }, [token, authHeader]);

  const loadUsers = async (q = '') => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`,
        authHeader,
      );
      setUsers(data.users || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load users');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (isAdmin === true) loadUsers();
  }, [isAdmin]);

  // Debounced search
  useEffect(() => {
    if (isAdmin !== true) return;
    const t = setTimeout(() => loadUsers(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query, isAdmin]);

  const handleToggleEnterprise = async (u) => {
    if (u.is_whitelisted_owner && u.is_enterprise) {
      toast.error("Whitelisted owners can't be demoted");
      return;
    }
    setPendingId(u.id);
    try {
      const { data } = await axios.patch(
        `${API}/admin/users/${u.id}`,
        { is_enterprise: !u.is_enterprise },
        authHeader,
      );
      setUsers((prev) => prev.map((x) => (x.id === u.id ? data.user : x)));
      toast.success(data.user.is_enterprise
        ? `Enterprise access granted to ${data.user.email}`
        : `Enterprise access removed from ${data.user.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setPendingId(null); }
  };

  const handleToggleDisabled = async (u) => {
    if (u.is_whitelisted_owner) {
      toast.error("Whitelisted owners can't be disabled");
      return;
    }
    setPendingId(u.id);
    try {
      if (u.is_disabled) {
        // Re-enable via PATCH
        const { data } = await axios.patch(
          `${API}/admin/users/${u.id}`,
          { is_disabled: false },
          authHeader,
        );
        setUsers((prev) => prev.map((x) => (x.id === u.id ? data.user : x)));
        toast.success(`Account re-enabled: ${u.email}`);
      } else {
        await axios.delete(`${API}/admin/users/${u.id}`, authHeader);
        setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, is_disabled: true } : x)));
        toast.success(`Account disabled: ${u.email}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setPendingId(null); }
  };

  // ─── Gates ─────────────────────────────────────────────
  if (isAdmin === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }
  if (isAdmin === false) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#F7F5F0' }}>
        <Card className="max-w-md w-full p-8 text-center rounded-3xl border border-stone-200 bg-white" data-testid="admin-gate">
          <Shield className="w-12 h-12 text-stone-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2" style={{ color: CHARCOAL }}>Admins only</h1>
          <p className="text-sm text-stone-600 mb-6">
            This page is restricted to system administrators of Plexura AI Scribe.
          </p>
          <Button onClick={() => navigate('/')} variant="outline" className="rounded-xl">Back to dashboard</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F7F5F0' }}>
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Button onClick={() => navigate('/')} variant="outline" className="rounded-xl shrink-0 px-3 sm:px-4" data-testid="admin-back-btn">
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2" style={{ color: CHARCOAL, fontFamily: 'Manrope, sans-serif' }}>
                <Shield className="w-5 h-5" style={{ color: INDIGO }} />
                User administration
                <span className="inline-flex items-center gap-1 bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full">
                  Admin
                </span>
              </h1>
              <p className="text-xs text-stone-500 truncate">
                {users.length} user{users.length !== 1 ? 's' : ''} · grant Enterprise, create accounts, disable access
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => navigate('/admin/revenue')}
              variant="outline"
              data-testid="open-revenue-dashboard-btn"
              className="h-10 rounded-xl border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800 font-semibold"
            >
              <TrendingUp className="w-4 h-4 mr-2" /> Revenue
            </Button>
            <Button
              onClick={() => setShowCreate(true)}
              className="h-10 rounded-xl text-white font-semibold"
              style={{ background: INDIGO }}
              data-testid="open-create-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Create user
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by email or name…"
            className="pl-9 h-11 rounded-xl bg-white"
            data-testid="admin-search-input"
          />
        </div>

        <Card className="p-0 rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="users-table-card">
          {loading ? (
            <div className="py-20 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            </div>
          ) : users.length === 0 ? (
            <div className="py-20 text-center text-sm text-stone-500">
              {query ? 'No users match your search.' : 'No users yet — click "Create user" to add the first one.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="bg-stone-50">
                  <tr className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
                    <th className="text-left py-3 px-4">User</th>
                    <th className="text-left py-3 px-4 hidden md:table-cell">Practice</th>
                    <th className="text-left py-3 px-4 hidden lg:table-cell">Created</th>
                    <th className="text-left py-3 px-4">Tier</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <UserRow
                      key={u.id}
                      user={u}
                      pending={pendingId === u.id}
                      onToggleEnterprise={() => handleToggleEnterprise(u)}
                      onToggleDisabled={() => handleToggleDisabled(u)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </main>

      {showCreate && (
        <CreateUserDialog
          authHeader={authHeader}
          onClose={() => setShowCreate(false)}
          onCreated={(newUser) => {
            // Add to list, but DON'T close the dialog — the success panel
            // inside the dialog needs to render so admin can copy the
            // auto-generated password before dismissing.
            setUsers((prev) => [newUser, ...prev]);
          }}
        />
      )}
    </div>
  );
}

// ─── User row ──────────────────────────────────────────────
function UserRow({ user, pending, onToggleEnterprise, onToggleDisabled }) {
  return (
    <tr
      className={`border-t border-stone-100 ${user.is_disabled ? 'opacity-50' : ''}`}
      data-testid={`user-row-${user.id}`}
    >
      <td className="py-3 px-4 align-top">
        <div className="flex items-start gap-2 min-w-0">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-semibold text-stone-900 truncate">{user.full_name || '(no name)'}</span>
              {user.is_whitelisted_owner && (
                <span className="inline-flex items-center gap-1 text-indigo-700 text-[10px] font-bold uppercase tracking-wide">
                  <Crown className="w-3 h-3" /> Owner
                </span>
              )}
              {user.is_disabled && (
                <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded">
                  <Ban className="w-3 h-3" /> Disabled
                </span>
              )}
            </div>
            <div className="text-xs text-stone-500 truncate">{user.email}</div>
          </div>
        </div>
      </td>
      <td className="py-3 px-4 align-top hidden md:table-cell">
        {user.practice_role
          ? <span className="text-xs text-stone-700 capitalize">{user.practice_role}</span>
          : <span className="text-xs text-stone-400">—</span>}
      </td>
      <td className="py-3 px-4 align-top hidden lg:table-cell">
        <span className="text-xs text-stone-600">{fmtDate(user.created_at)}</span>
      </td>
      <td className="py-3 px-4 align-top">
        {user.is_enterprise ? (
          <span className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-800 text-xs font-semibold px-2 py-0.5 rounded-full" data-testid={`tier-badge-${user.id}`}>
            <Sparkles className="w-3 h-3" /> Enterprise
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 bg-stone-100 text-stone-700 text-xs font-medium px-2 py-0.5 rounded-full" data-testid={`tier-badge-${user.id}`}>
            <Clock className="w-3 h-3" /> Trial / Pro
          </span>
        )}
      </td>
      <td className="py-3 px-4 align-top">
        <div className="flex justify-end gap-2 flex-wrap">
          <Button
            size="sm"
            variant={user.is_enterprise ? 'outline' : 'default'}
            onClick={onToggleEnterprise}
            disabled={pending || (user.is_whitelisted_owner && user.is_enterprise)}
            className={`rounded-lg text-xs h-8 px-3 ${user.is_enterprise ? '' : 'text-white'}`}
            style={user.is_enterprise ? {} : { background: INDIGO }}
            data-testid={`toggle-enterprise-${user.id}`}
            title={user.is_whitelisted_owner ? 'Whitelisted owners always have Enterprise' : ''}
          >
            {pending
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : user.is_enterprise
                ? <><XCircle className="w-3.5 h-3.5 mr-1" /> Revoke</>
                : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Make Enterprise</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onToggleDisabled}
            disabled={pending || user.is_whitelisted_owner}
            className="rounded-lg text-xs h-8 px-3"
            data-testid={`toggle-disabled-${user.id}`}
          >
            {user.is_disabled
              ? <><RotateCcw className="w-3.5 h-3.5 mr-1" /> Re-enable</>
              : <><Ban className="w-3.5 h-3.5 mr-1" /> Disable</>}
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ─── Create-user dialog ────────────────────────────────────
function CreateUserDialog({ authHeader, onClose, onCreated }) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [isEnterprise, setIsEnterprise] = useState(true);
  // Default to magic-link — better HIPAA story; admin can opt back to a
  // shared password for in-person setups via the toggle.
  const [magicLink, setMagicLink] = useState(true);
  const [sendWelcome, setSendWelcome] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null); // { user, generated_password, magic_link_sent }
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const body = {
        email: email.trim(),
        full_name: fullName.trim(),
        is_enterprise: isEnterprise,
        magic_link: magicLink,
        // When magic_link is on, the magic email IS the welcome email,
        // so the secondary "send welcome email" toggle is irrelevant.
        send_welcome_email: magicLink ? false : sendWelcome,
      };
      if (!magicLink && password.trim()) body.password = password.trim();
      const { data } = await axios.post(`${API}/admin/users`, body, authHeader);
      setCreated(data);
      onCreated(data.user);
      toast.success(data.magic_link_sent ? 'Magic link sent' : 'User created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally { setSubmitting(false); }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(created.generated_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="create-user-dialog">
      <Card className="bg-white rounded-2xl w-full max-w-md p-6">
        {!created ? (
          <>
            <h2 className="text-lg font-bold mb-1" style={{ color: CHARCOAL }}>Create user</h2>
            <p className="text-xs text-stone-500 mb-4">
              {magicLink
                ? 'A one-tap "Set your password" link will be emailed — no password leaves the server.'
                : 'A welcome email with sign-in instructions will be sent.'}
            </p>
            <div className="space-y-3">
              <div>
                <Label htmlFor="cu-email" className="text-xs font-semibold">Email *</Label>
                <Input id="cu-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                       className="mt-1 h-10 rounded-xl" data-testid="cu-email" autoFocus />
              </div>
              <div>
                <Label htmlFor="cu-name" className="text-xs font-semibold">Full name *</Label>
                <Input id="cu-name" value={fullName} onChange={(e) => setFullName(e.target.value)}
                       className="mt-1 h-10 rounded-xl" data-testid="cu-name" />
              </div>

              {/* Setup method toggle — magic link (recommended) vs password share */}
              <div
                className="rounded-xl border-2 p-3 transition-colors cursor-pointer"
                style={{
                  borderColor: magicLink ? INDIGO : '#E7E5E4',
                  background: magicLink ? '#EEF2FF' : '#FAFAF9',
                }}
                onClick={() => setMagicLink(true)}
                data-testid="cu-method-magic"
              >
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={magicLink}
                    onChange={() => setMagicLink(true)}
                    className="mt-0.5 w-4 h-4 accent-indigo-600 cursor-pointer"
                    aria-label="Send magic link"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold" style={{ color: CHARCOAL }}>
                        Send a magic link
                      </span>
                      <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded">
                        <Shield className="w-3 h-3" />
                        HIPAA-friendly
                      </span>
                    </div>
                    <p className="text-[11px] text-stone-600 mt-0.5">
                      The user clicks a 72-hour link in email and sets their own password. No plaintext credentials are ever shared.
                    </p>
                  </div>
                </label>
              </div>
              <div
                className="rounded-xl border-2 p-3 transition-colors cursor-pointer"
                style={{
                  borderColor: !magicLink ? INDIGO : '#E7E5E4',
                  background: !magicLink ? '#EEF2FF' : '#FAFAF9',
                }}
                onClick={() => setMagicLink(false)}
                data-testid="cu-method-password"
              >
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!magicLink}
                    onChange={() => setMagicLink(false)}
                    className="mt-0.5 w-4 h-4 accent-indigo-600 cursor-pointer"
                    aria-label="Set password manually"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold" style={{ color: CHARCOAL }}>
                      Set a temporary password
                    </div>
                    <p className="text-[11px] text-stone-600 mt-0.5">
                      Auto-generates a one-time password (or use your own) — useful for in-person onboarding when you can read it out loud.
                    </p>
                    {!magicLink && (
                      <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                        <Input
                          id="cu-pw"
                          type="text"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Auto-generated if blank"
                          className="h-9 rounded-lg font-mono text-sm"
                          data-testid="cu-password"
                        />
                      </div>
                    )}
                  </div>
                </label>
              </div>

              <label className="flex items-center gap-2 cursor-pointer pt-1">
                <input type="checkbox" checked={isEnterprise}
                       onChange={(e) => setIsEnterprise(e.target.checked)}
                       data-testid="cu-enterprise"
                       className="w-4 h-4 rounded accent-indigo-600" />
                <span className="text-sm">
                  Grant <strong>Enterprise</strong> access
                  <span className="text-stone-500"> (skip the trial)</span>
                </span>
              </label>
              {!magicLink && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={sendWelcome}
                         onChange={(e) => setSendWelcome(e.target.checked)}
                         data-testid="cu-send-email"
                         className="w-4 h-4 rounded accent-indigo-600" />
                  <span className="text-sm">Send welcome email with credentials</span>
                </label>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-5">
              <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
              <Button onClick={submit}
                      disabled={submitting || !email.trim() || !fullName.trim()}
                      className="rounded-xl text-white" style={{ background: INDIGO }}
                      data-testid="cu-submit">
                {submitting
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : magicLink ? <><Mail className="w-4 h-4 mr-1" /> Send magic link</> : 'Create user'}
              </Button>
            </div>
          </>
        ) : (
          // Confirmation panel
          <>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-bold" style={{ color: CHARCOAL }}>
                {created.magic_link_sent ? 'Magic link sent' : 'User created'}
              </h2>
            </div>
            <p className="text-sm text-stone-600 mb-4">
              <strong>{created.user.email}</strong>{' '}
              {created.magic_link_sent
                ? 'will receive a one-tap "Set your password" link in their inbox. The link expires in 72 hours.'
                : 'can now sign in to Plexura.'}
              {created.user.is_enterprise && ' Enterprise access is active.'}
            </p>
            {created.generated_password && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                <p className="text-[11px] font-semibold uppercase text-amber-800 mb-1">Auto-generated password</p>
                <div className="flex items-center gap-2">
                  <code className="font-mono text-sm flex-1 bg-white px-2 py-1.5 rounded border border-amber-200 select-all">
                    {created.generated_password}
                  </code>
                  <Button size="sm" variant="outline" onClick={copyPassword} className="rounded-lg h-8" data-testid="cu-copy-password">
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-[11px] text-amber-700 mt-2">
                  Save this — you won't see it again. It was emailed to the user as well.
                </p>
              </div>
            )}
            {created.magic_link_sent && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 mb-4 flex items-start gap-2" data-testid="cu-magic-confirmation">
                <Mail className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
                <div className="text-xs text-indigo-900 leading-relaxed">
                  No password leaves the server. The user lands on the standard password-set page from their email.
                </div>
              </div>
            )}
            <Button onClick={onClose} className="w-full rounded-xl text-white" style={{ background: INDIGO }} data-testid="cu-close-success">
              Done
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
